import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { BdShareablesService } from '@bindoc-projects/bindoc-permissions-lib/data';
import {
  accessOriginOf,
  AccessGraphEntry,
  AccessGraphShareTargetRef,
  BD_SHARE_PERMISSION_DISPLAY_NAME,
  BdSharePermission,
  BdSharePermissionHelper,
  SHAREABLE_TYPE_DISPLAY_NAME,
  ShareableType,
} from '@bindoc-projects/resource-domain-lib';
import { Button } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ProgressSpinner } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { take } from 'rxjs';

export interface ArchiveShareableDialogData {
  shareableType: ShareableType;
  shareableId: string;
  shareableName?: string;
}

export interface ConsumerEntry {
  shareTarget: AccessGraphShareTargetRef;
  allowedShareablePermissions: BdSharePermission[];
  permissionPer: 'folder' | 'grants';
}

@Component({
  selector: 'bd-archive-shareable-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-4 p-4">
      <div class="flex items-start gap-3">
        <i class="pi pi-exclamation-triangle text-2xl text-bd-warning"></i>
        <div class="flex flex-col gap-1">
          <p class="m-0 font-medium">
            {{ headlineMessage() }}
          </p>
          <p class="m-0 text-sm text-muted-color">
            Archivierte Elemente werden in den Papierkorb verschoben und können
            später wiederhergestellt werden.
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium">{{ consumerHeadline() }}</span>
        @if (loading()) {
          <div class="flex justify-center py-3">
            <p-progress-spinner class="w-6 h-6" />
          </div>
        } @else if (errorMessage()) {
          <span class="text-sm text-bd-error">{{ errorMessage() }}</span>
        } @else if (consumers().length === 0) {
          <span class="text-sm text-muted-color">
            Außer Ihnen hat aktuell niemand Zugriff auf dieses Element.
          </span>
        } @else {
          <p-table
            [value]="consumers()"
            [paginator]="consumers().length > 5"
            [rows]="5"
            stripedRows
            dataKey="shareTarget.id"
          >
            <ng-template #header>
              <tr>
                <th>Wer</th>
                <th>Zugriff via</th>
                <th>Berechtigung</th>
              </tr>
            </ng-template>
            <ng-template #body let-entry>
              <tr>
                <td>
                  <div class="flex flex-row items-center gap-2">
                    <i [class]="iconFor(entry)"></i>
                    <span>{{ displayName(entry) }}</span>
                  </div>
                </td>
                <td>
                  {{ entry.permissionPer === 'folder' ? 'Ordner' : 'Freigabe' }}
                </td>
                <td>
                  {{ permissionLabel(entry.allowedShareablePermissions) }}
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      </div>

      <div
        class="flex flex-row gap-4 justify-end w-full pt-4 flex-shrink-0 sticky-footer"
      >
        <!-- 03 Abort: grey text, so it does not compete with the danger action. -->
        <p-button
          label="Abbrechen"
          severity="secondary"
          text
          (click)="cancel()"
          data-test="archive-shareable-cancel"
        />
        <p-button
          label="Ja, archivieren"
          severity="danger"
          icon="pi pi-inbox"
          (click)="confirm()"
          data-test="archive-shareable-confirm"
        />
      </div>
    </div>
  `,
  imports: [Button, ProgressSpinner, TableModule],
})
export class BdArchiveShareableDialogComponent implements OnInit {
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config =
    inject<DynamicDialogConfig<ArchiveShareableDialogData>>(
      DynamicDialogConfig,
    );
  private readonly _resourcesService = inject(BdShareablesService);

  public readonly loading = signal(true);
  public readonly errorMessage = signal('');
  public readonly consumers = signal<ConsumerEntry[]>([]);

  public readonly headlineMessage = computed(() => {
    const data = this._config.data;
    const typeLabel = data
      ? (SHAREABLE_TYPE_DISPLAY_NAME[data.shareableType] ?? 'Element')
      : 'Element';
    const name = data?.shareableName?.trim();
    return name
      ? `Möchten Sie ${typeLabel} „${name}“ wirklich archivieren?`
      : `Möchten Sie dieses ${typeLabel} wirklich archivieren?`;
  });

  public readonly consumerHeadline = computed(() => {
    const count = this.consumers().length;
    if (count === 0) {
      return 'Auswirkungen';
    }
    return count === 1
      ? '1 weitere Person/Gruppe verliert den Zugriff:'
      : `${count} weitere Personen/Gruppen verlieren den Zugriff:`;
  });

  ngOnInit(): void {
    const data = this._config.data;
    if (!data?.shareableType || !data?.shareableId) {
      this._ref.close(false);
      return;
    }
    this._loadConsumers(data.shareableType, data.shareableId);
  }

  public cancel(): void {
    this._ref.close(false);
  }

  public confirm(): void {
    this._ref.close(true);
  }

  /**
   * The access graph resolves every share target's `name` server-side
   * (including descriptive share-group labels the current user cannot resolve
   * from their own groups, BDA-5618). Use it directly, falling back to the id.
   */
  public displayName(entry: ConsumerEntry): string {
    return entry.shareTarget.name?.trim() || entry.shareTarget.id;
  }

  public iconFor(entry: ConsumerEntry): string {
    switch (entry.shareTarget.type) {
      case 'user':
        return 'pi pi-user';
      case 'group':
        return 'pi pi-users';
      case 'company':
        return 'pi pi-building';
      default:
        return 'pi pi-question';
    }
  }

  public permissionLabel(permissions: BdSharePermission[]): string {
    const highest = BdSharePermissionHelper.getHighestPermission(permissions);
    return BD_SHARE_PERMISSION_DISPLAY_NAME['de'][highest] ?? highest;
  }

  private _loadConsumers(type: ShareableType, id: string): void {
    // No company context and no `expand=users`: archiving revokes access for
    // every anchor in every context, so the warning must show the unfiltered
    // union rather than what happens to apply in the selected Kunde.
    this._resourcesService
      .accessGraph(type, id)
      .pipe(take(1))
      .subscribe({
        next: (graph) => {
          this.consumers.set(graph.entries.map((e) => this._toEntry(e)));
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.errorMessage.set(
            err instanceof HttpErrorResponse && err.status === 403
              ? 'Sie haben keine Berechtigung, die Zugriffsübersicht dieser Ressource anzuzeigen.'
              : 'Zugriffsübersicht konnte nicht geladen werden.',
          );
          this.loading.set(false);
        },
      });
  }

  /**
   * An anchor whose access comes from a containing folder is labelled "Ordner",
   * which is what the removed endpoint's `folders`/`grants` grouping conveyed.
   * An anchor with both kinds of path counts as folder-derived, because that is
   * the part archiving does not visibly explain.
   */
  private _toEntry(raw: AccessGraphEntry): ConsumerEntry {
    const viaFolder = raw.paths.some(
      (path) => accessOriginOf(path) === 'folder',
    );
    return {
      shareTarget: raw.shareTarget,
      allowedShareablePermissions: raw.permissions,
      permissionPer: viaFolder ? 'folder' : 'grants',
    };
  }
}
